/**
 * Transition sets of the default-purchase process that BOTH the client and the
 * server need to reason about.
 *
 * Why this file exists: `transactionProcessPurchase.js` (ESM) owns the process
 * graph, but the server cannot `require()` it — `yarn test-server` is plain Jest
 * with no `transform`, and `server/index.js` runs on bare Node with no Babel, so
 * an `export const` throws `SyntaxError: Unexpected token 'export'` at the first
 * request. Until now the workaround in server/ was to re-type the transition
 * names by hand (see `server/scripts/inspect-transaction.js`,
 * `server/scripts/poll-confirm-payment-emails.js`, both carrying a "Keep in sync
 * with src/…" comment). That drift is exactly what a money-guard must not rely on.
 *
 * Authored as CommonJS so the server can `require()` it directly, while
 * client-side ESM `import` still works (babel-preset-env interops CJS exports).
 * `src/util/shippingFee.js` is the same shape, but note it is NOT a precedent for
 * the bundling half: it is only ever required from a server test
 * (`server/api-util/lineItems.test.js:600`). This file is the first CJS module in
 * `src/` that both production server code requires AND webpack bundles for the
 * browser, via the `import` in `transactionProcessPurchase.js`.
 *
 * The names themselves stay owned by `transactionProcessPurchase.js#transitions`;
 * `purchaseTransitionSets.test.js` asserts every string below is a value of that
 * map, so a typo here fails the suite instead of silently disabling a guard.
 */

// Transitions that give the buyer their money back.
//
// The money guard and the orphan detector both test these against the transition
// HISTORY, not against `lastTransition`. Testing the last one only is equivalent
// *while* `payment-expired` and `canceled` are terminal — true in this repo's copy
// of process.edn, which the repo itself declares non-authoritative for Live. The
// history test is free and holds either way. The UI helpers below (isRefunded, used
// by the breakdown and the activity feed) still ask about a single transition; that
// is a display question, not a money one.
const REFUNDED_TRANSITIONS = [
  'transition/expire-payment',
  'transition/cancel',
  'transition/auto-cancel',
  'transition/auto-cancel-from-disputed',
  'transition/cancel-from-disputed',
];

// Transitions that settle the order (payout done, review window running or over).
// States reached: completed, reviewed-by-customer, reviewed-by-provider, reviewed.
const COMPLETED_TRANSITIONS = [
  'transition/auto-complete',
  'transition/review-1-by-customer',
  'transition/review-1-by-provider',
  'transition/review-2-by-customer',
  'transition/review-2-by-provider',
  'transition/expire-review-period',
  'transition/expire-customer-review-period',
  'transition/expire-provider-review-period',
];

// The single transition that commits the buyer's payment. It is the ONLY edge
// into state/purchased, but callers must look for it in the transition HISTORY,
// not in `lastTransition` — a paid order moves on (delivered, received, …) and
// `confirm-payment` is almost never the last transition.
const CONFIRM_PAYMENT_TRANSITION = 'transition/confirm-payment';

const isRefundedTransition = transition => REFUNDED_TRANSITIONS.includes(transition);

const isCompletedTransition = transition => COMPLETED_TRANSITIONS.includes(transition);

module.exports = {
  REFUNDED_TRANSITIONS,
  COMPLETED_TRANSITIONS,
  CONFIRM_PAYMENT_TRANSITION,
  isRefundedTransition,
  isCompletedTransition,
};
